import React, { useEffect, useRef } from 'react';
import { useUser } from '../../../contexts/UserContext';
import { useToast } from '../../../contexts/ToastContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useBusinessDetailsQuery } from '../../../hooks/useBusinessDetails';

import StatisticsSummary from './StatisticsSummary/StatisticsSummary';
import BookingsRevenueChart from './BookingsRevenueChart/BookingsRevenueChart';
import AppointmentStatistics from './AppointmentStatistics/AppointmentStatistics';
import RecentPayments, { PaymentMethodBreakdownCard } from './RecentPayments/RecentPayments';

import styles from './Dashboard.module.css';
import RevenueChart from './RevenueChart/RevenueChart';
import DateRangePicker from '../../../components/DateRangePicker/DateRangePicker';
import BranchFilter from '../../../components/BranchFilter/BranchFilter';
import { useDashboard } from '../../../hooks/useDashboard';
import Button from '../../../components/Button/Button';
import MetricCard from '../../../components/Dashboard/MetricCard/MetricCard';
import TrialBanner from '../../../components/TrialBanner/TrialBanner';
// AB#1904 (W-1) — one-time post-upgrade welcome banner (sessionStorage one-shot).
import PostUpgradeBanner from '../../../components/PostUpgradeBanner/PostUpgradeBanner';
import {
    useCurrentSubscription,
    subscriptionStatusToNumber,
} from '../../../hooks/useCurrentSubscription';

const RevenueIcon = () => (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect style={{ fill: 'var(--success-tint)' }} width="40" height="40" rx="14" />
        <path style={{ stroke: 'var(--success-fg)' }} d="M20 12V28" strokeWidth="1.5" strokeLinecap="round" />
        <path style={{ stroke: 'var(--success-fg)' }} d="M24 16C24 14.3431 22.2091 13 20 13C17.7909 13 16 14.3431 16 16C16 17.6569 17.7909 19 20 19C22.2091 19 24 20.3431 24 22C24 23.6569 22.2091 25 20 25C17.7909 25 16 23.6569 16 22" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);

const Dashboard: React.FC = () => {
    const { user } = useUser();
    const { showToast } = useToast();
    const { t } = useLanguage();
    // AB#1982 (AC-3) — tenant currency (JOD/AED) from the business profile
    // (AB#1682 `currencyIso`), cached via TanStack Query. Falls back to AED —
    // the dashboard's prior hardcoded currency — when unresolved. Never '$'.
    const { data: business } = useBusinessDetailsQuery();
    const currency = business?.currencyIso ?? 'AED';
    const {
        loading,
        range,
        setRange,
        setSelectedDate,
        summary,
        bookingsRevenue,
        revenueSeries,
        appointmentStats,
        totalPaymentRevenue,
        recentPayments,
        paymentMethodBreakdown,
        refresh,
        lastSuccessfulLoad,
        hasLoaded,
        error,
    } = useDashboard({ range: 'last30days' });

    // #1326: Only show the "Dashboard updated successfully" toast on MANUAL
    // refresh, not on initial mount/navigation. Firing on every load produced
    // a misleading toast (e.g. when users hit /promotions and were silently
    // redirected to /, the dashboard's load-complete toast made it look like
    // a dashboard update had been performed).
    const manualRefreshPendingRef = useRef(false);
    const initialLoadSeenRef = useRef(false);
    useEffect(() => {
        if (lastSuccessfulLoad == null) return;
        if (!initialLoadSeenRef.current) {
            initialLoadSeenRef.current = true;
            return;
        }
        if (manualRefreshPendingRef.current) {
            manualRefreshPendingRef.current = false;
            showToast(t('dashboard.toast.refreshSuccess'), 'success');
        }
    }, [lastSuccessfulLoad, showToast, t]);

    const handleManualRefresh = async () => {
        manualRefreshPendingRef.current = true;
        await refresh();
    };

    // PBI-C — show trial/past-due banner above the greeting when relevant.
    const { data: subscription } = useCurrentSubscription();
    const subscriptionStatusNum = subscriptionStatusToNumber(subscription?.status);

    return (
        <div className={styles.dashboard} aria-busy={loading}>

            {loading && (
                <div className={styles.loadingOverlay}>
                    <div className={styles.spinner}>
                        <svg viewBox="0 0 50 50">
                            <circle cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
                        </svg>
                        <span>{t('dashboard.loading')}</span>
                    </div>
                </div>
            )}

            {/* AB#1904 (W-1) — one-time post-upgrade welcome banner.
                Renders only when sessionStorage has the upgrade flag set
                by /subscription/success. Takes precedence over a stale
                Trial banner; we don't show both at once. */}
            <PostUpgradeBanner />

            {subscription && subscriptionStatusNum > 0 && (
                <TrialBanner
                    status={subscriptionStatusNum}
                    trialEndAt={subscription.trialEndAt}
                    planName={subscription.planName}
                    isFoundingMember={subscription.isFoundingMember}
                />
            )}

            <div className={styles.clientNameGreetingAndDateFilterContainer}>
                <h1 className={styles.clientName}>
                    {t('dashboard.greeting').replace('{name}', user?.fullName || 'User')}
                </h1>
                <div className={styles.greetingAndDateFilter}>
                    <h3 className={styles.greetingText}>
                      {/* #1180: Dynamic subtitle based on selected filter */}
                      {t('dashboard.subtitle').replace(
                        '{period}',
                        range === 'today'
                            ? t('dashboard.period.today')
                            : range === 'last7days'
                                ? t('dashboard.period.week')
                                : range === 'last30days'
                                    ? t('dashboard.period.month')
                                    : t('dashboard.period.overall'),
                      )}
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <DateRangePicker
                            value={range}
                            onChange={(r, date) => {
                                setRange(r);
                                if (date !== undefined) setSelectedDate(date);
                            }}
                        />
                        <Button
                            label={loading ? t('dashboard.refreshing') : t('dashboard.refresh')}
                            size="small"
                            variant="secondary"
                            disabled={loading}
                            onClick={handleManualRefresh}
                        />
                    </div>
                </div>
            </div>

            <BranchFilter />

            {/* O1 (AB#2199, SO-02 / SO-43) — a failed load says so; it never shows zeros as data. */}
            {error && !loading && (
                <div className={styles.loadError} role="alert">
                    <span>{t('dashboard.loadFailed')}</span>
                    <Button label={t('dashboard.retry')} size="small" variant="secondary" onClick={handleManualRefresh} />
                </div>
            )}

            <StatisticsSummary
                totalAppointments={summary.totalAppointments}
                employees={summary.employees}
                activeBranches={summary.activeBranches}
                loaded={hasLoaded}
            />

            {/* Total Payment Revenue Card */}
            <div className={styles.revenueMetricRow}>
                <MetricCard
                    // O1 UAT — like the KPI cards, a placeholder (never "0.00") until the first load.
                    value={hasLoaded
                        ? `${totalPaymentRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`
                        : '—'}
                    label={t('dashboard.metric.totalPaymentRevenue')}
                    icon={<RevenueIcon />}
                    iconVariant="purple"
                    iconSize="large"
                />
            </div>

            <BookingsRevenueChart
                labels={bookingsRevenue.labels}
                bookings={bookingsRevenue.bookings}
                amounts={bookingsRevenue.amounts}
                totalRevenue={bookingsRevenue.totalRevenue}
                reservationRate={bookingsRevenue.reservationRate}
                currency={currency}
                loaded={hasLoaded}
            />
            <div className={styles.charts}>
                <RevenueChart data={revenueSeries.data} labels={revenueSeries.labels} year={revenueSeries.year} currency={currency} />
                <AppointmentStatistics labels={appointmentStats.labels} values={appointmentStats.values} />
            </div>

            {/* Payment section: Recent Payments + Breakdown */}
            <div className={styles.charts}>
                <RecentPayments payments={recentPayments} />
                <PaymentMethodBreakdownCard breakdown={paymentMethodBreakdown} />
            </div>
        </div>
    );
};

export default Dashboard;
